// General date helpers shared across frontend and backend. Formatting lives in
// `format.ts`; this module is for parsing/normalization primitives.
/** Zero-padded `YYYY-MM` with a valid month (01–12). */
export const MONTH_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;
/**
 * Coerces an arbitrary date-ish string into the `YYYY-MM` shape a month picker
 * (`<input type="month">`) expects, or `''` when no month can be derived.
 *
 * Useful for values that are only loosely date-shaped — e.g. LLM extraction or
 * website scraping returning `"2021"`, `"2021-03-15"`, or prose ("early 2022").
 * Seeding those straight into a month input leaves the field blank while the raw
 * string lingers in form state and later crashes `new Date()` server-side.
 *
 * Handles the machine-shaped cases (`YYYY-MM`, leading ISO date/datetime, bare
 * year). Anything else returns `''` so the user picks a value explicitly.
 */
export function normalizeToMonth(value) {
    if (!value)
        return '';
    const trimmed = value.trim();
    if (!trimmed)
        return '';
    if (MONTH_REGEX.test(trimmed))
        return trimmed;
    // Leading ISO date/datetime, e.g. "2021-03-15" or "2021-03-15T00:00:00Z".
    const iso = trimmed.match(/^(\d{4})-(\d{2})\b/);
    if (iso) {
        const month = Number(iso[2]);
        if (month >= 1 && month <= 12)
            return `${iso[1]}-${iso[2]}`;
    }
    // Bare year, e.g. "2021" → January of that year.
    const year = trimmed.match(/^(\d{4})$/);
    if (year)
        return `${year[1]}-01`;
    return '';
}
/**
 * Safely converts a date-ish string into a month-precision `Date` (first of the
 * month, UTC) or `null`. Never throws.
 *
 * Guards values that are only validated as strings upstream so they never reach
 * a raw `new Date()` (which yields an "Invalid Date" that Prisma rejects).
 */
export function parseMonth(value) {
    const normalized = normalizeToMonth(value);
    if (!normalized)
        return null;
    const date = new Date(`${normalized}-01T00:00:00.000Z`);
    return Number.isNaN(date.getTime()) ? null : date;
}
/**
 * The product's reporting timezone. Weekly figures are framed in local business
 * time rather than UTC so a Monday-morning number means what a founder in
 * Prague thinks it means.
 */
export const REPORTING_TIME_ZONE = 'Europe/Prague';
/**
 * Start (inclusive) and end (exclusive) of the local week containing `now`,
 * as UTC instants ready to hand to a database.
 *
 * Weeks start Monday. The offset is read back out of the zone for the given
 * instant rather than assumed, so the DST-transition weeks are 23 or 25 hours
 * long instead of silently dropping or double-counting an hour's activity.
 */
export function localWeekBounds(now, timeZone = REPORTING_TIME_ZONE) {
    const parts = zonedParts(now, timeZone);
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    // Monday-based: Sunday closes the week it ends, rather than opening a new one.
    const daysSinceMonday = (weekdays.indexOf(parts.weekday) + 6) % 7;
    const monday = parts.day - daysSinceMonday;
    // Both edges are resolved as local midnights in their own right. Deriving the
    // end as `from + 168h` would assume every week is the same length, which is
    // exactly what a transition week isn't.
    return {
        from: localMidnightUtc(parts.year, parts.month, monday, timeZone),
        to: localMidnightUtc(parts.year, parts.month, monday + 7, timeZone),
    };
}
/**
 * How many days of history a send-rate reading averages over. Shared so the
 * sidebar badge and Home's runway cannot drift onto different windows and quote
 * different numbers for the same campaign.
 */
export const SEND_RATE_WINDOW_DAYS = 7;
/**
 * A trailing window ending at `now`, for rate measurements.
 *
 * Deliberately not a calendar week: dividing a week-to-date count by 7 reads as
 * a slow campaign every Monday, which is exactly when a draining queue most
 * needs to be visible.
 */
export function trailingWindow(now, days = SEND_RATE_WINDOW_DAYS) {
    return { from: new Date(now.getTime() - days * 24 * 60 * 60 * 1000), to: now };
}
/** The zone's wall-clock reading of an instant, as numbers. */
function zonedParts(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(instant);
    const part = (type) => parts.find((p) => p.type === type)?.value ?? '00';
    return {
        weekday: part('weekday'),
        year: Number(part('year')),
        month: Number(part('month')),
        day: Number(part('day')),
        hour: Number(part('hour')),
        minute: Number(part('minute')),
        second: Number(part('second')),
    };
}
/** How far the zone runs ahead of UTC at a given instant, in milliseconds. */
function zoneOffsetMs(instant, timeZone) {
    const p = zonedParts(instant, timeZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - instant.getTime();
}
/**
 * The UTC instant at which a local calendar day began. `month` is 1-based and
 * `day` may over- or underflow its month, as `Date.UTC` allows.
 *
 * The offset is sampled twice: once at the naive guess, then again at the
 * candidate instant that guess produces. On a transition day the two readings
 * differ, and only the second one belongs to the day actually being resolved.
 */
function localMidnightUtc(year, month, day, timeZone) {
    const wallClock = Date.UTC(year, month - 1, day);
    const guess = wallClock - zoneOffsetMs(new Date(wallClock), timeZone);
    return new Date(wallClock - zoneOffsetMs(new Date(guess), timeZone));
}
//# sourceMappingURL=date.js.map